import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, Sparkles, Moon, Sun, ArrowRight, CheckCircle, Leaf, ShoppingCart, Percent } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

const protocolHerbs = [
  {
    name: "Vitex (Chasteberry)",
    handle: "vitex",
    benefit: "Directly supports the pituitary gland's communication with the ovaries. One of the most researched herbs for menstrual and hormonal regularity.",
    dosage: "20-40mg standardized extract daily",
    icon: Heart
  },
  {
    name: "Black Cohosh",
    handle: "black-cohosh",
    benefit: "Traditionally used for menstrual discomfort, hot flashes, and hormonal transition symptoms. Well-studied for menopausal support.",
    dosage: "20-40mg standardized extract daily",
    icon: Sparkles
  },
  {
    name: "Dong Quai",
    handle: "dong-quai",
    benefit: "Known as 'female ginseng' in Traditional Chinese Medicine. Supports healthy blood flow, menstrual comfort, and hormonal balance.",
    dosage: "500-1,000mg daily",
    icon: Moon
  },
  {
    name: "Ashwagandha",
    handle: "ashwagandha",
    benefit: "Reduces cortisol levels that disrupt estrogen and progesterone production. Supports sleep, mood, and stress resilience.",
    dosage: "300-600mg standardized extract daily",
    icon: Sun
  },
  {
    name: "Nettle Leaf",
    handle: "nettle-leaf",
    benefit: "Rich in minerals that support hormonal health. Helps with water retention, inflammation, and provides nutritive support.",
    dosage: "1-2 cups nettle tea daily or 300mg extract",
    icon: Leaf
  }
];

const WomensProtocol = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-pink-700 to-rose-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Heart className="w-10 h-10 text-pink-200" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="womens-protocol-title">
            Support Hormonal Balance, Energy & Mood — Naturally
          </h1>
          <p className="text-xl text-pink-100 max-w-2xl mx-auto">
            A thoughtfully crafted herbal protocol for women navigating the natural rhythms of life after 30.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        {/* Understanding Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Understanding Your Body's Signals</h2>
          <p className="text-gray-600 text-lg leading-relaxed">
            Hormonal shifts after 30 are common. Stress and lifestyle can disrupt rhythm and recovery, 
            affecting everything from mood to energy levels. These changes don't have to define your experience — 
            with the right natural support, you can find your balance again.
          </p>
        </div>

        {/* What This Protocol Supports */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">What This Protocol Supports</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-pink-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Balanced Mood</h3>
              <p className="text-gray-600">
                Adaptogenic and nervine herbs that support emotional equilibrium throughout your cycle.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <Sun className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Stable Energy</h3>
              <p className="text-gray-600">
                Support consistent energy levels without the crashes, naturally aligned with your body's rhythms.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Hormonal Rhythm</h3>
              <p className="text-gray-600">
                Traditional botanicals known for supporting healthy hormonal cycles and balance.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-md border border-gray-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Moon className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Restorative Sleep</h3>
              <p className="text-gray-600">
                Calming herbs that promote deep, restful sleep and support nighttime recovery.
              </p>
            </div>
          </div>
        </div>

        {/* Protocol Herbs - Individual Buy Links */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Your Balance Protocol Herbs</h2>
          <p className="text-gray-500 text-center mb-8">Each herb plays a specific role in supporting hormonal harmony. Purchase individually or build your complete stack.</p>
          
          <div className="space-y-4" data-testid="protocol-herbs-list">
            {protocolHerbs.map((herb, idx) => {
              const IconComp = herb.icon;
              return (
                <div key={herb.name} className="bg-white rounded-xl shadow-md border border-gray-100 p-6 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 bg-pink-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComp className="w-6 h-6 text-pink-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{herb.name}</h3>
                      <p className="text-sm text-gray-600 mt-1">{herb.benefit}</p>
                      <p className="text-xs text-pink-700 font-medium mt-1">Suggested: {herb.dosage}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate(`/store?q=${encodeURIComponent(herb.name.split(" (")[0])}`)}
                    className="bg-pink-600 hover:bg-pink-700 rounded-full flex-shrink-0"
                    size="sm"
                    data-testid={`buy-${herb.handle}`}
                  >
                    <ShoppingCart className="mr-2 w-4 h-4" />
                    Buy Now
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 text-center space-y-3">
            <Button
              onClick={() => { addItem("bundle:womens_balance"); toast.success("Women's Balance Protocol Bundle added to cart — 10% off!"); navigate("/cart"); }}
              className="rounded-full bg-pink-600 hover:bg-pink-700 h-12 px-8 font-semibold shadow-lg"
              data-testid="add-womens-bundle-btn"
            >
              <Percent className="mr-2 w-4 h-4" />
              Add Complete Protocol Bundle — Save 10%
            </Button>
            <div>
            <Button
              onClick={() => navigate("/store")}
              variant="outline"
              className="rounded-full"
              data-testid="browse-all-herbs"
            >
              Browse All Herbs <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            </div>
          </div>
        </div>

        {/* Who This Is For */}
        <div className="bg-pink-50 rounded-2xl p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Who This Is For</h2>
          <p className="text-gray-700 text-lg mb-6">
            This protocol is designed for women feeling "off," fatigued, or experiencing subtle imbalance signs. 
            If something feels different and you're ready for natural support — this is for you.
          </p>
          <ul className="space-y-3">
            {[
              "Women 30+ experiencing mood or energy shifts",
              "Those noticing changes in sleep quality or cycle regularity",
              "Anyone seeking natural hormone balance support",
              "Women wanting to feel like themselves again"
            ].map((item) => (
              <li key={item} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl p-8 text-center text-white">
          <Leaf className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h2 className="text-2xl font-bold mb-4">Want a Personalized Plan?</h2>
          <p className="text-pink-100 mb-8 max-w-lg mx-auto">
            Get AI-powered herb recommendations tailored to your unique health profile, symptoms, and goals.
          </p>
          <Link to="/questionnaire">
            <Button
              className="bg-white text-pink-700 hover:bg-pink-50 px-8 py-4 text-lg font-semibold rounded-full"
              data-testid="get-personalized-plan"
            >
              Get My Personalized Plan <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>

        {/* Back Link */}
        <div className="text-center mt-8">
          <Link to="/womens-assessment" className="text-pink-600 hover:text-pink-700 font-medium">
            &larr; Take the Hormone Assessment First
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WomensProtocol;
